const APP_BUTTON_SELECTOR = '.Vtl-StickyAddToCart__AddToCartButton';
const BUY_NOW_TEXT = 'Buy it now';

/**
 * Keeps the Vitals sticky bar label in sync even when the app re-renders it.
 * Only the existing text node is changed so the app's icon and styling remain intact.
 *
 * @param {Element} button
 */
function updateButtonLabel(button) {
  const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    if (textNode.textContent?.trim().toLowerCase() === 'add to cart') {
      textNode.textContent = BUY_NOW_TEXT;
    }
    textNode = walker.nextNode();
  }

  button.setAttribute('aria-label', BUY_NOW_TEXT);
}

/**
 * @returns {Array<{id: string|number, title?: string, available?: boolean}>}
 */
function getProductVariants() {
  return window.vtlsLiquidData?.product?.variants ?? [];
}

/**
 * @param {Element} button
 * @returns {Element}
 */
function getStickyBar(button) {
  let stickyBar = button;
  let ancestor = button.parentElement;

  while (ancestor && ancestor !== document.body) {
    if (ancestor.getAttribute('class')?.includes('StickyAddToCart')) {
      stickyBar = ancestor;
    }
    ancestor = ancestor.parentElement;
  }

  return stickyBar;
}

/**
 * Finds the selected variant without depending on the app's private implementation.
 *
 * @param {Element} button
 * @returns {string}
 */
function getSelectedVariantId(button) {
  const stickyBar = getStickyBar(button);
  const variants = getProductVariants();
  const variantIds = new Set(variants.map((variant) => String(variant.id)));
  const candidates = [
    button.getAttribute('data-variant-id'),
    stickyBar.getAttribute('data-variant-id'),
    ...Array.from(stickyBar.querySelectorAll('[data-variant-id]')).map((element) =>
      element.getAttribute('data-variant-id')
    ),
    ...Array.from(stickyBar.querySelectorAll('select, input')).flatMap((element) => {
      if (element instanceof HTMLSelectElement) {
        const selectedOption = element.selectedOptions[0];
        return [selectedOption?.getAttribute('data-variant-id'), selectedOption?.value, element.value];
      }

      if (element instanceof HTMLInputElement && (element.checked || element.type === 'hidden')) {
        return [element.getAttribute('data-variant-id'), element.value];
      }

      return [];
    }),
  ];

  for (const candidate of candidates) {
    if (candidate && variantIds.has(String(candidate))) return String(candidate);
  }

  // The app exposes the chosen option labels in the bar. Match those labels to
  // its product data before falling back to the theme's main product form.
  const stickyBarText = stickyBar.textContent?.replace(/\s+/g, ' ').toLowerCase() ?? '';
  const textMatch = variants.find((variant) =>
    String(variant.title ?? '')
      .split('/')
      .map((option) => option.trim().toLowerCase())
      .filter(Boolean)
      .every((option) => stickyBarText.includes(option))
  );
  if (textMatch) return String(textMatch.id);

  const mainVariantInput = document.querySelector(
    'main product-form-component input[name="id"], main form[action*="/cart/add"] input[name="id"]'
  );
  if (mainVariantInput instanceof HTMLInputElement && variantIds.has(mainVariantInput.value)) {
    return mainVariantInput.value;
  }

  const urlVariantId = new URLSearchParams(window.location.search).get('variant');
  if (urlVariantId && variantIds.has(urlVariantId)) return urlVariantId;

  const firstAvailableVariant = variants.find((variant) => variant.available !== false);
  return firstAvailableVariant ? String(firstAvailableVariant.id) : '';
}

/**
 * @param {Element} button
 * @returns {number}
 */
function getSelectedQuantity(button) {
  const stickyBar = getStickyBar(button);
  const quantityInput = stickyBar.querySelector('input[name="quantity"], input[type="number"]');
  const mainQuantityInput = document.querySelector(
    'main product-form-component input[name="quantity"], main form[action*="/cart/add"] input[name="quantity"]'
  );
  const rawQuantity =
    (quantityInput instanceof HTMLInputElement ? quantityInput.value : '') ||
    (mainQuantityInput instanceof HTMLInputElement ? mainQuantityInput.value : '') ||
    '1';
  const quantity = Number.parseInt(rawQuantity, 10);

  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

/**
 * Sends the selected variant and quantity straight to Shopify Checkout.
 * Shopify cart permalinks open Checkout by default.
 *
 * @param {Event} event
 */
function handleVitalsBuyNow(event) {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest(APP_BUTTON_SELECTOR);
  if (!button || button.hasAttribute('disabled')) return;

  const variantId = getSelectedVariantId(button);
  if (!/^\d+$/.test(variantId)) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const quantity = getSelectedQuantity(button);
  const root = window.Shopify?.routes?.root ?? '/';
  window.location.assign(`${root}cart/${variantId}:${quantity}`);
}

function refreshAppButtons() {
  document.querySelectorAll(APP_BUTTON_SELECTOR).forEach(updateButtonLabel);
}

document.addEventListener('click', handleVitalsBuyNow, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshAppButtons, { once: true });
} else {
  refreshAppButtons();
}

new MutationObserver(refreshAppButtons).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
